import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download } from 'lucide-react';

import { apiClient } from '../lib/api.js';
import type { TulpaDetail } from '@arx-marketplace/shared/tulpas';
import { cn, formatVersion } from '../lib/utils.js';

export function TulpaPage(): JSX.Element {
  const { slug = '' } = useParams();
  const [data, setData] = useState<TulpaDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .getTulpa(slug)
      .then(setData)
      .catch((e: Error) => setErr(e.message));
  }, [slug]);

  if (err) return <div className="container py-12 text-destructive">{err}</div>;
  if (!data) return <div className="container py-12 text-muted-foreground">Loading…</div>;

  return (
    <article className="container py-10 flex-1 max-w-4xl">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Tulpa</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">{data.name}</h1>
          <div className="mt-2 flex items-center gap-1.5 flex-wrap text-sm text-muted-foreground">
            {data.engines.map((e) => (
              <span key={e} className="px-1.5 py-0.5 rounded bg-muted">
                {e}
              </span>
            ))}
            {data.default_model && <span>· {data.default_model}</span>}
            <span>· {data.hosting_mode}</span>
            {data.latest_version && <code>{formatVersion(data.latest_version).label}</code>}
            {data.owner_name && <span>· by {data.owner_name}</span>}
          </div>
          <p className="mt-3 text-muted-foreground">{data.summary}</p>
        </div>
        {data.latest_version && (
          <a
            href={apiClient.tulpaDownloadUrl(data.slug)}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-2 rounded-md shrink-0',
              'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors',
            )}
          >
            <Download className="h-4 w-4" />
            Download .tulpa
          </a>
        )}
      </header>

      {data.persona_excerpt && (
        <section className="mt-10">
          <h2 className="text-xl font-semibold">Persona</h2>
          <pre className="mt-3 rounded-md border bg-card p-4 text-sm whitespace-pre-wrap font-sans leading-relaxed">
            {data.persona_excerpt}
            {data.persona_excerpt.length >= 600 ? '…' : ''}
          </pre>
        </section>
      )}

      {data.description && (
        <section className="mt-10">
          <h2 className="text-xl font-semibold">About</h2>
          <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed">
            {data.description}
          </pre>
        </section>
      )}

      <section className="mt-10">
        <h2 className="text-xl font-semibold">Bundled talents ({data.talents.length})</h2>
        {data.talents.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">This tulpa bundles no talents.</p>
        ) : (
          <table className="mt-4 w-full text-sm">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left font-normal py-2">Talent</th>
                <th className="text-left font-normal py-2">Version</th>
                <th className="text-left font-normal py-2">SHA-256</th>
              </tr>
            </thead>
            <tbody>
              {data.talents.map((t) => (
                <tr key={t.slug} className="border-t">
                  <td className="py-2 font-medium">{t.slug}</td>
                  <td className="py-2">
                    <code>{formatVersion(t.version).label}</code>
                  </td>
                  <td className="py-2 text-muted-foreground">
                    <code className="text-xs">{t.sha256.slice(0, 12)}…</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-semibold">Versions</h2>
        <table className="mt-4 w-full text-sm">
          <thead className="text-muted-foreground">
            <tr>
              <th className="text-left font-normal py-2">Version</th>
              <th className="text-left font-normal py-2">Size</th>
              <th className="text-left font-normal py-2">Released</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.versions.map((v) => (
              <tr key={v.id} className="border-t">
                <td className="py-2">
                  <code>{formatVersion(v.version).label}</code>
                </td>
                <td className="py-2 text-muted-foreground">{formatBytes(v.size_bytes)}</td>
                <td className="py-2 text-muted-foreground">
                  {new Date(v.created_at).toLocaleDateString()}
                </td>
                <td className="py-2 text-right">
                  <a
                    href={apiClient.tulpaDownloadUrl(data.slug, v.version)}
                    className="text-sm underline-offset-4 hover:underline"
                  >
                    download
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </article>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
